import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, Search } from "lucide-react";
import { BRAND, NAV } from "@/data/site";
import { DuckBadge } from "@/components/DuckMascot";
import { useHeaderTheme } from "@/components/HeaderThemeContext";
import { cn } from "@/lib/utils";

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [pestsOpen, setPestsOpen] = useState(false);
  const location = useLocation();
  const { variant } = useHeaderTheme();
  const light = !scrolled && variant !== "solid"; // light text over dark heroes

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setPestsOpen(false);
  }, [location.pathname]);

  return (
    <header
      className={cn(
        "fixed top-0 inset-x-0 z-50 transition-all duration-300",
        scrolled || variant === "solid"
          ? "bg-cream/85 backdrop-blur-xl border-b border-forest/10 shadow-[0_2px_20px_rgba(30,58,43,0.06)]"
          : "bg-transparent"
      )}
      data-testid="site-header"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
        <div className="flex items-center justify-between h-[72px]">
          <Link to="/" className="flex items-center gap-2.5 group" data-testid="header-logo-link">
            <DuckBadge size={44} />
            <span className={cn("font-serif text-xl font-bold tracking-tight transition-colors", light ? "text-cream" : "text-forest-950")}>
              Duck<span className="text-terracotta">The</span>Bugs
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-1">
            {NAV.map((item) =>
              item.children ? (
                <div
                  key={item.label}
                  className="relative"
                  onMouseEnter={() => setPestsOpen(true)}
                  onMouseLeave={() => setPestsOpen(false)}
                >
                  <Link
                    to={item.to}
                    className={cn("flex items-center gap-1 px-4 py-2 text-sm font-medium rounded-full transition-colors", light ? "text-cream/85 hover:text-cream hover:bg-cream/10" : "text-forest-950/80 hover:text-forest-950 hover:bg-pill/60")}
                    data-testid="nav-pests"
                  >
                    {item.label}
                    <ChevronDown className="w-3.5 h-3.5 opacity-60" />
                  </Link>
                  {pestsOpen && (
                    <div className="absolute top-full left-0 pt-2 w-56">
                      <div className="bg-cream rounded-2xl border border-forest/10 shadow-xl p-2">
                        {item.children.map((c) => (
                          <Link
                            key={c.to}
                            to={c.to}
                            className="block px-4 py-2.5 text-sm text-forest-950/80 hover:text-forest-950 hover:bg-pill/70 rounded-xl transition-colors"
                            data-testid={`nav-dropdown-${c.to.split("/").pop()}`}
                          >
                            {c.label}
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  key={item.to}
                  to={item.to}
                  className={cn("px-4 py-2 text-sm font-medium rounded-full transition-colors", light ? "text-cream/85 hover:text-cream hover:bg-cream/10" : "text-forest-950/80 hover:text-forest-950 hover:bg-pill/60")}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {item.label}
                </Link>
              )
            )}
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            <Link
              to="/guides"
              aria-label="Search guides"
              className={cn("p-2.5 rounded-full transition-colors", light ? "text-cream/80 hover:bg-cream/10" : "text-forest-950/70 hover:bg-pill/60")}
              data-testid="header-search-link"
            >
              <Search className="w-[18px] h-[18px]" />
            </Link>
            <Link
              to="/hire-a-pro"
              className="px-5 py-2.5 rounded-full bg-terracotta text-white text-sm font-semibold hover:bg-terracotta/90 transition-all hover:shadow-lg hover:shadow-terracotta/20 active:scale-95"
              data-testid="header-find-pro-btn"
            >
              Find a Pro
            </Link>
          </div>

          <button
            onClick={() => setMobileOpen((v) => !v)}
            className={cn("lg:hidden p-2 rounded-full transition-colors", light ? "text-cream hover:bg-cream/10" : "text-forest-950 hover:bg-pill/60")}
            aria-label="Toggle menu"
            data-testid="mobile-menu-toggle"
          >
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="lg:hidden bg-cream border-t border-forest/10 max-h-[calc(100vh-72px)] overflow-y-auto" data-testid="mobile-menu">
          <div className="px-4 py-4 space-y-1">
            {NAV.map((item) => (
              <div key={item.label}>
                <Link
                  to={item.to}
                  className="block px-4 py-3 text-base font-medium text-forest-950 rounded-xl hover:bg-pill/70"
                >
                  {item.label}
                </Link>
                {item.children && (
                  <div className="pl-4">
                    {item.children.map((c) => (
                      <Link
                        key={c.to}
                        to={c.to}
                        className="block px-4 py-2 text-sm text-forest-950/70 rounded-xl hover:bg-pill/70"
                      >
                        {c.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
            <Link
              to="/hire-a-pro"
              className="block mt-3 text-center px-5 py-3 rounded-full bg-terracotta text-white font-semibold"
              data-testid="mobile-find-pro-btn"
            >
              Find a Pro
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
